import { createFileRoute } from "@tanstack/react-router";
import { getPublicErrorMessageForError } from "@proxi/convex";
import { useMutation, useQuery } from "convex/react";
import { useEffect, useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/convex-api";

type Id<TableName extends string> = string & { __tableName: TableName };

type PrivateStatus =
	| "kyc_required"
	| "submitted_kyc_pending"
	| "under_review"
	| "quoted_awaiting_payment"
	| "payment_confirmed"
	| "in_progress"
	| "completed"
	| "cancelled";

type PrivateStatusFilter = PrivateStatus | "all";

const STATUS_LABEL: Record<PrivateStatus, string> = {
	kyc_required: "KYC Required",
	submitted_kyc_pending: "Submitted (KYC Pending)",
	under_review: "Under Review",
	quoted_awaiting_payment: "Quoted (Awaiting Payment)",
	payment_confirmed: "Payment Confirmed",
	in_progress: "In Progress",
	completed: "Completed",
	cancelled: "Cancelled",
};

const STATUS_OPTIONS: Array<{ value: PrivateStatus; label: string }> = (
	Object.keys(STATUS_LABEL) as Array<PrivateStatus>
).map((value) => ({
	value,
	label: STATUS_LABEL[value],
}));

export const Route = createFileRoute("/_authenticated/private")({
	component: PrivateTasksPage,
});

function PrivateTasksPage() {
	const [statusFilter, setStatusFilter] = useState<PrivateStatusFilter>("all");
	const [selectedTaskId, setSelectedTaskId] =
		useState<Id<"privateTasks"> | null>(null);
	const [draftStatus, setDraftStatus] = useState<PrivateStatus>("kyc_required");
	const [staffNotes, setStaffNotes] = useState("");
	const [quotedPriceNaira, setQuotedPriceNaira] = useState("");
	const [paymentReference, setPaymentReference] = useState("");
	const [kycRejectionReason, setKycRejectionReason] = useState("");
	const [savingMessage, setSavingMessage] = useState<string | null>(null);
	const [isSaving, setIsSaving] = useState(false);
	const [isSavingKyc, setIsSavingKyc] = useState(false);

	const taskRows = useQuery(api.privateTasks.getAllPrivateTasks as any, {
		status: statusFilter === "all" ? undefined : statusFilter,
		limit: 100,
	}) as
		| Array<{
				id: Id<"privateTasks">;
				status: PrivateStatus;
				customerName: string;
				customerEmail: string;
				category: string;
				customCategory?: string;
				urgency: string;
				createdAt: number;
		  }>
		| undefined;

	const selectedTask = useQuery(
		api.privateTasks.getPrivateTask,
		selectedTaskId ? { privateTaskId: selectedTaskId } : "skip",
	);

	const selectedCustomerKyc = useQuery(
		api.privateTasks.getCustomerPrivateKycForAdmin,
		selectedTask?.customer?.id
			? { customerId: selectedTask.customer.id as Id<"users"> }
			: "skip",
	);

	const updatePrivateTaskStatus = useMutation(
		api.privateTasks.updatePrivateTaskStatus as any,
	);
	const updatePrivateCustomerKycStatus = useMutation(
		api.privateTasks.updatePrivateCustomerKycStatus,
	);

	useEffect(() => {
		if (!selectedTask) return;
		setDraftStatus(selectedTask.status as PrivateStatus);
		setStaffNotes(selectedTask.staffNotes ?? "");
		setQuotedPriceNaira(
			selectedTask.quotedPriceKobo
				? String(Math.floor(selectedTask.quotedPriceKobo / 100))
				: "",
		);
		setPaymentReference(selectedTask.paymentReference ?? "");
	}, [selectedTask]);

	const sortedRows = useMemo(() => taskRows ?? [], [taskRows]);

	const handleSaveTaskUpdate = async () => {
		if (!selectedTaskId) return;
		if (draftStatus === "quoted_awaiting_payment" && !quotedPriceNaira.trim()) {
			setSavingMessage(
				"Quoted price is required before moving to quoted status.",
			);
			return;
		}

		setIsSaving(true);
		setSavingMessage(null);
		try {
			const quotedPriceValue = quotedPriceNaira.trim()
				? Math.round(Number(quotedPriceNaira.trim()) * 100)
				: undefined;

			await updatePrivateTaskStatus({
				privateTaskId: selectedTaskId,
				status: draftStatus,
				staffNotes: staffNotes.trim() || undefined,
				quotedPriceKobo:
					Number.isFinite(quotedPriceValue) &&
					quotedPriceValue &&
					quotedPriceValue > 0
						? quotedPriceValue
						: undefined,
				paymentReference: paymentReference.trim() || undefined,
			});
			setSavingMessage("Private task updated.");
		} catch (error) {
			setSavingMessage(getPublicErrorMessageForError(error));
		} finally {
			setIsSaving(false);
		}
	};

	const handleVerifyKyc = async () => {
		if (!selectedTask?.customer?.id) return;
		setIsSavingKyc(true);
		setSavingMessage(null);
		try {
			await updatePrivateCustomerKycStatus({
				customerId: selectedTask.customer.id as Id<"users">,
				status: "verified",
			});
			setSavingMessage("Customer private KYC verified.");
		} catch (error) {
			setSavingMessage(getPublicErrorMessageForError(error));
		} finally {
			setIsSavingKyc(false);
		}
	};

	const handleRejectKyc = async () => {
		if (!selectedTask?.customer?.id) return;
		if (!kycRejectionReason.trim()) {
			setSavingMessage("Provide a rejection reason.");
			return;
		}
		setIsSavingKyc(true);
		setSavingMessage(null);
		try {
			await updatePrivateCustomerKycStatus({
				customerId: selectedTask.customer.id as Id<"users">,
				status: "rejected",
				rejectionReason: kycRejectionReason.trim(),
			});
			setSavingMessage("Customer private KYC rejected.");
		} catch (error) {
			setSavingMessage(getPublicErrorMessageForError(error));
		} finally {
			setIsSavingKyc(false);
		}
	};

	return (
		<div className="container mx-auto px-4 py-8 lg:px-6">
			<div className="mb-6 rounded-2xl border bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 px-6 py-5 text-white">
				<div className="flex items-center justify-between">
					<div>
						<h1 className="font-bold text-3xl">Private Tasks</h1>
						<p className="mt-1 text-sm text-slate-200">
							High-trust queue for sensitive customer requests.
						</p>
					</div>
					<Select
						onValueChange={(value) =>
							setStatusFilter(value as PrivateStatusFilter)
						}
						value={statusFilter}
					>
						<SelectTrigger className="w-[220px] border-slate-600 bg-slate-800 text-white">
							<SelectValue placeholder="Filter by status" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">All statuses</SelectItem>
							{STATUS_OPTIONS.map((option) => (
								<SelectItem key={option.value} value={option.value}>
									{option.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
			</div>

			<div className="grid gap-6 lg:grid-cols-[1.2fr_1fr]">
				<Card>
					<CardHeader>
						<CardTitle>Queue</CardTitle>
					</CardHeader>
					<CardContent>
						{taskRows === undefined ? (
							<p>Loading private tasks...</p>
						) : sortedRows.length === 0 ? (
							<p className="text-muted-foreground">No private tasks found.</p>
						) : (
							<div className="rounded-md border">
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead>Customer</TableHead>
											<TableHead>Category</TableHead>
											<TableHead>Urgency</TableHead>
											<TableHead>Status</TableHead>
											<TableHead>Created</TableHead>
											<TableHead>Action</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{sortedRows.map((task: (typeof sortedRows)[number]) => (
											<TableRow
												className={
													selectedTaskId === task.id ? "bg-slate-50" : ""
												}
												key={task.id}
											>
												<TableCell>
													<div className="font-medium">{task.customerName}</div>
													<div className="text-muted-foreground text-xs">
														{task.customerEmail}
													</div>
												</TableCell>
												<TableCell className="max-w-[220px]">
													<div className="truncate">
														{task.customCategory || task.category}
													</div>
												</TableCell>
												<TableCell>{task.urgency}</TableCell>
												<TableCell>
													<Badge variant="secondary">
														{STATUS_LABEL[task.status as PrivateStatus] ??
															task.status}
													</Badge>
												</TableCell>
												<TableCell>
													{new Date(task.createdAt).toLocaleDateString()}
												</TableCell>
												<TableCell>
													<Button
														onClick={() => setSelectedTaskId(task.id)}
														size="sm"
														variant="outline"
													>
														Select
													</Button>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</div>
						)}
					</CardContent>
				</Card>

				<Card>
					<CardHeader>
						<CardTitle>Task Detail</CardTitle>
					</CardHeader>
					<CardContent className="space-y-4">
						{!selectedTaskId ? (
							<p className="text-muted-foreground">
								Select a task from the queue.
							</p>
						) : selectedTask === undefined ? (
							<p>Loading task detail...</p>
						) : (
							<>
								<div className="space-y-1">
									<p className="font-medium">
										{selectedTask?.customer?.name ?? "Unknown customer"}
									</p>
									<p className="text-muted-foreground text-sm">
										{selectedTask?.customer?.email ?? "No email"}
									</p>
									<p className="text-muted-foreground text-sm">
										{selectedTask?.customer?.phoneNumber ?? "No phone"}
									</p>
								</div>

								<div className="rounded-lg border p-3">
									<p className="font-medium text-sm">Private KYC</p>
									<p className="text-muted-foreground text-sm">
										Status: {selectedCustomerKyc?.status ?? "not_submitted"}
									</p>
									{selectedCustomerKyc?.status === "pending" ? (
										<div className="mt-3 space-y-2">
											<Button
												disabled={isSavingKyc}
												onClick={handleVerifyKyc}
												size="sm"
											>
												Verify KYC
											</Button>
											<Textarea
												onChange={(event) =>
													setKycRejectionReason(event.target.value)
												}
												placeholder="Reason for KYC rejection"
												value={kycRejectionReason}
											/>
											<Button
												disabled={isSavingKyc}
												onClick={handleRejectKyc}
												size="sm"
												variant="destructive"
											>
												Reject KYC
											</Button>
										</div>
									) : null}
								</div>

								<div className="space-y-2">
									<p className="font-medium text-sm">Description</p>
									<p className="text-sm">{selectedTask?.description}</p>
								</div>

								<div className="space-y-2">
									<p className="font-medium text-sm">Status</p>
									<Select
										onValueChange={(value) =>
											setDraftStatus(value as PrivateStatus)
										}
										value={draftStatus}
									>
										<SelectTrigger>
											<SelectValue placeholder="Select status" />
										</SelectTrigger>
										<SelectContent>
											{STATUS_OPTIONS.map((option) => (
												<SelectItem key={option.value} value={option.value}>
													{option.label}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>

								<div className="space-y-2">
									<p className="font-medium text-sm">Quoted price (Naira)</p>
									<Input
										onChange={(event) =>
											setQuotedPriceNaira(event.target.value)
										}
										placeholder="e.g. 125000"
										type="number"
										value={quotedPriceNaira}
									/>
								</div>

								<div className="space-y-2">
									<p className="font-medium text-sm">Payment reference</p>
									<Input
										onChange={(event) =>
											setPaymentReference(event.target.value)
										}
										placeholder="Bank transfer reference"
										value={paymentReference}
									/>
								</div>

								<div className="space-y-2">
									<p className="font-medium text-sm">Staff notes</p>
									<Textarea
										onChange={(event) => setStaffNotes(event.target.value)}
										placeholder="Internal notes for this request"
										value={staffNotes}
									/>
								</div>

								<Button disabled={isSaving} onClick={handleSaveTaskUpdate}>
									{isSaving ? "Saving..." : "Save update"}
								</Button>

								{savingMessage ? (
									<p className="text-muted-foreground text-sm">
										{savingMessage}
									</p>
								) : null}
							</>
						)}
					</CardContent>
				</Card>
			</div>
		</div>
	);
}
